import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { InsightIcon } from '#/shared/ui/icons'

import { useSuggestedPriceSd } from '../model/use-suggested-price-sd'
import { MarketRateStatsBadge } from './market-rate-stats-badge'

interface MarketRateCheckProps {
  shipmentId: string | undefined
}

/**
 * Market-rate trigger for the Payment header. Idle → "Check Rate" link;
 * fetching → spinner; resolved → Super Dispatch recommended price, per-mile
 * rate, and provider confidence; failed → the backend reason. The resolved
 * badge is itself a button — clicking it re-runs the check (DEV-676).
 */
export function MarketRateCheck({ shipmentId }: MarketRateCheckProps) {
  const { t } = useTranslation()
  const [checked, setChecked] = useState(false)
  const { stats, isFetching, errorMessage, refetch } = useSuggestedPriceSd({
    shipmentId,
    enabled: checked,
  })

  const handleCheck = () => {
    setChecked(true)
    refetch()
  }

  // Nothing to price against until the shipment exists (e.g. mid-create).
  if (shipmentId === undefined) return null

  if (!checked) {
    return (
      <button
        type="button"
        onClick={handleCheck}
        className="flex shrink-0 cursor-pointer items-center gap-1.25 text-sm font-normal text-gray-900"
      >
        <InsightIcon className="size-4 shrink-0 text-orange-500" />
        <span className="whitespace-nowrap underline">
          {t('orderDetails.payment.checkRate')}
        </span>
      </button>
    )
  }

  // A click always re-runs the request — surface the spinner so a retry over a
  // stale "insufficient data" result is visibly acknowledged (DEV-676).
  if (isFetching) {
    return (
      <span className="flex items-center gap-1.25 text-sm font-normal text-gray-500">
        <span className="size-4 animate-spin rounded-full border-2 border-orange-500 border-t-transparent" />
        {t('orderDetails.payment.checkingRate')}
      </span>
    )
  }

  if (errorMessage !== undefined) {
    return (
      <button
        type="button"
        onClick={handleCheck}
        title={t('orderDetails.payment.checkRateRetryHint')}
        className="flex max-w-48 cursor-pointer items-center gap-1.25 text-xs font-normal text-gray-500"
      >
        <InsightIcon className="size-4 shrink-0 text-gray-400" />
        <span className="truncate">{errorMessage}</span>
      </button>
    )
  }

  if (!stats) return null

  return (
    <button
      type="button"
      onClick={handleCheck}
      title={t('orderDetails.payment.checkRateRetryHint')}
      className="flex cursor-pointer items-center gap-1.25 font-normal"
    >
      <InsightIcon className="size-4 text-orange-500" />
      <MarketRateStatsBadge stats={stats} />
    </button>
  )
}
